/**
 * Recipe tag parsing and structured filtering.
 *   [0] difficulty  — Fácil | Media | Difícil
 *   [1] cuisine     — Mexicana, Italiana, Universal, …
 *   [2] season      — Todo el año, Otoño · Invierno, …
 *   [3] protein     — Pollo, Res, lácteos, …
 *   [4+] allergens  — lácteos, gluten, huevo, mariscos, soya, pescado, …
 *
 * Legacy recipes use freeform tags without positional meaning.
 */

import { normalizeSearchText } from './textSearch';

const DIFFICULTIES = new Set(['facil', 'fácil', 'media', 'dificil', 'difícil']);

const ALLERGEN_KEYWORDS = [
  'lacteos',
  'lácteos',
  'gluten',
  'huevo',
  'mariscos',
  'soya',
  'pescado',
  'frutos secos',
  'cerdo',
];

export const ALLERGEN_OPTIONS = [
  { key: 'lacteos', label: 'Lácteos' },
  { key: 'gluten', label: 'Gluten' },
  { key: 'huevo', label: 'Huevo' },
  { key: 'mariscos', label: 'Mariscos' },
  { key: 'soya', label: 'Soya' },
  { key: 'pescado', label: 'Pescado' },
  { key: 'frutos secos', label: 'Frutos secos' },
  { key: 'cerdo', label: 'Cerdo' },
];

export const EMPTY_TAG_FILTERS = {
  difficulty: [],
  cuisine: [],
  season: [],
  protein: [],
  excludeAllergens: [],
  legacyTags: [],
};

export function normalizeTagText(value) {
  return normalizeSearchText(value);
}

export function parseTagsArray(tags) {
  if (Array.isArray(tags)) {
    return tags.map((t) => String(t).trim()).filter(Boolean);
  }
  if (typeof tags === 'string' && tags.trim()) {
    const trimmed = tags.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map((t) => t.replace(/^"|"$/g, '').trim())
        .filter(Boolean);
    }
    return trimmed.split(',').map((t) => t.trim()).filter(Boolean);
  }
  return [];
}

function isDifficultyTag(tag) {
  return DIFFICULTIES.has(normalizeTagText(tag));
}

function isAllergenTag(tag) {
  const n = normalizeTagText(tag);
  return ALLERGEN_KEYWORDS.some((keyword) => n.includes(normalizeTagText(keyword)));
}

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, 'es', { sensitivity: 'base' })
  );
}

/**
 * Parse tags into structured dimensions when the recipe follows the 5-slot schema.
 */
export function parseRecipeTagProfile(tags) {
  const all = parseTagsArray(tags);
  if (all.length === 0) {
    return {
      structured: false,
      all: [],
      difficulty: [],
      cuisine: [],
      season: [],
      protein: [],
      allergens: [],
      legacyTags: [],
    };
  }

  if (!isDifficultyTag(all[0])) {
    return {
      structured: false,
      all,
      difficulty: [],
      cuisine: [],
      season: [],
      protein: [],
      allergens: all.filter(isAllergenTag),
      legacyTags: all,
    };
  }

  const allergensFromTail = all.slice(4).filter(Boolean);
  const proteinTag = all[3] || '';
  const proteinAllergen = proteinTag && isAllergenTag(proteinTag);

  return {
    structured: true,
    all,
    difficulty: all[0] ? [all[0]] : [],
    cuisine: all[1] ? [all[1]] : [],
    season: all[2] ? [all[2]] : [],
    protein: all[3] && !proteinAllergen ? [all[3]] : [],
    allergens: [
      ...allergensFromTail.filter(isAllergenTag),
      ...(proteinAllergen ? [all[3]] : []),
    ],
    legacyTags: allergensFromTail.filter((t) => !isAllergenTag(t)),
  };
}

function tagMatchesSelection(tagValue, selectedValues) {
  if (!selectedValues?.length) return true;
  const normalizedTag = normalizeTagText(tagValue);
  return selectedValues.some((selected) => {
    const normalizedSelected = normalizeTagText(selected);
    return (
      normalizedTag === normalizedSelected ||
      normalizedTag.includes(normalizedSelected) ||
      normalizedSelected.includes(normalizedTag)
    );
  });
}

function dimensionMatches(profileValues, selectedValues) {
  if (!selectedValues?.length) return true;
  return profileValues.some((value) => tagMatchesSelection(value, selectedValues));
}

function recipeContainsAllergen(profile, allergenKey) {
  const key = normalizeTagText(allergenKey);
  return profile.all.some((tag) => normalizeTagText(tag).includes(key));
}

/**
 * Build filter chip options from the loaded recipe list.
 */
export function buildTagFilterOptions(recipes) {
  const difficulty = [];
  const cuisine = [];
  const season = [];
  const protein = [];
  const legacyTags = [];

  (recipes || []).forEach((recipe) => {
    const profile = parseRecipeTagProfile(recipe.tags);
    if (profile.structured) {
      difficulty.push(...profile.difficulty);
      cuisine.push(...profile.cuisine);
      season.push(...profile.season);
      protein.push(...profile.protein);
      legacyTags.push(...profile.legacyTags);
    } else {
      legacyTags.push(...profile.legacyTags);
    }
  });

  return {
    difficulty: uniqueSorted(difficulty),
    cuisine: uniqueSorted(cuisine),
    season: uniqueSorted(season),
    protein: uniqueSorted(protein),
    legacyTags: uniqueSorted(legacyTags).slice(0, 40),
  };
}

export function hasActiveTagFilters(tagFilters = EMPTY_TAG_FILTERS) {
  return (
    tagFilters.difficulty?.length > 0 ||
    tagFilters.cuisine?.length > 0 ||
    tagFilters.season?.length > 0 ||
    tagFilters.protein?.length > 0 ||
    tagFilters.excludeAllergens?.length > 0 ||
    tagFilters.legacyTags?.length > 0
  );
}

export function countActiveFilters(filters = {}) {
  let count = 0;
  if (filters.category) count += 1;
  if (filters.isPublic !== undefined) count += 1;
  const tagFilters = filters.tagFilters || EMPTY_TAG_FILTERS;
  count += (tagFilters.difficulty?.length || 0);
  count += (tagFilters.cuisine?.length || 0);
  count += (tagFilters.season?.length || 0);
  count += (tagFilters.protein?.length || 0);
  count += (tagFilters.legacyTags?.length || 0);
  count += (tagFilters.excludeAllergens?.length || 0);
  return count;
}

/** Flat list of active filter chips for toolbar display. */
export function getActiveFilterChips(filters = {}) {
  const chips = [];
  const tagFilters = filters.tagFilters || EMPTY_TAG_FILTERS;

  if (filters.category) {
    chips.push({ key: 'category', label: filters.category, clear: { category: '' } });
  }
  if (filters.isPublic === true) {
    chips.push({ key: 'public', label: 'Públicas', clear: { isPublic: undefined } });
  }
  if (filters.isPublic === false) {
    chips.push({ key: 'private', label: 'Privadas', clear: { isPublic: undefined } });
  }

  const dimensionLabels = {
    difficulty: 'Dificultad',
    cuisine: 'Cocina',
    season: 'Temporada',
    protein: 'Proteína',
    legacyTags: 'Tag',
  };

  Object.entries(dimensionLabels).forEach(([dim, prefix]) => {
    (tagFilters[dim] || []).forEach((value) => {
      chips.push({
        key: `${dim}-${value}`,
        label: `${prefix}: ${value}`,
        clear: {
          tagFilters: {
            ...tagFilters,
            [dim]: tagFilters[dim].filter((item) => item !== value),
          },
        },
      });
    });
  });

  (tagFilters.excludeAllergens || []).forEach((value) => {
    chips.push({
      key: `exclude-${value}`,
      label: `Sin ${value}`,
      clear: {
        tagFilters: {
          ...tagFilters,
          excludeAllergens: tagFilters.excludeAllergens.filter((item) => item !== value),
        },
      },
      variant: 'exclude',
    });
  });

  return chips;
}

/**
 * Returns true when a recipe satisfies structured tag filters.
 */
export function recipeMatchesTagFilters(recipe, tagFilters = EMPTY_TAG_FILTERS) {
  const profile = parseRecipeTagProfile(recipe.tags);
  const filters = { ...EMPTY_TAG_FILTERS, ...tagFilters };

  if (filters.excludeAllergens?.length) {
    const hasExcluded = filters.excludeAllergens.some((allergen) =>
      recipeContainsAllergen(profile, allergen)
    );
    if (hasExcluded) return false;
  }

  if (filters.legacyTags?.length) {
    const matchedLegacy = filters.legacyTags.some((selected) =>
      profile.all.some((tag) => tagMatchesSelection(tag, [selected]))
    );
    if (!matchedLegacy) return false;
  }

  if (!profile.structured) {
    const hasStructuredSelections =
      filters.difficulty?.length ||
      filters.cuisine?.length ||
      filters.season?.length ||
      filters.protein?.length;
    if (hasStructuredSelections && !filters.legacyTags?.length) {
      return false;
    }
    return true;
  }

  if (!dimensionMatches(profile.difficulty, filters.difficulty)) return false;
  if (!dimensionMatches(profile.cuisine, filters.cuisine)) return false;
  if (!dimensionMatches(profile.season, filters.season)) return false;
  if (!dimensionMatches(profile.protein, filters.protein)) return false;

  return true;
}

export function tagsToDisplayString(tags) {
  return parseTagsArray(tags).join(', ');
}
